import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ButtonComponent } from '@core/ui-kit/components';
import { TranslocoPipe } from '@jsverse/transloco';
import { DialogCloseDirective, DialogRef } from '@ngneat/dialog';
import { AuthStore } from '@shared/data-access/data-access-authentication';
import {
  PaymentMetadata,
  PaymentScope,
  Product,
  SUBSCRIPTION_PURCHASE_SUCCESS_KEY,
} from '@shared/data-access/data-access-payment';
import { TokenStore } from '@shared/data-access/data-access-token';

import { FlowMode, PlanAddonFlowComponent } from './plan-addon-flow/plan-addon-flow.component';

@Component({
  selector: 'app-subscription-plans-dialog',
  imports: [TranslocoPipe, PlanAddonFlowComponent, DialogCloseDirective, NgOptimizedImage, ButtonComponent],
  templateUrl: './subscription-plans-dialog.component.html',
  styleUrl: './subscription-plans-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionPlansDialogComponent {
  private readonly authStore = inject(AuthStore);
  private readonly tokenStore = inject(TokenStore);
  private readonly ref = inject(DialogRef);

  products = this.ref.data['products'];
  cyberExposureProducts = this.ref.data['cyberExposureProducts'];
  aiAddonProducts = this.ref.data['aiAddonProducts'];

  // An organisation that already pays for a plan gets the repeat-purchase flow.
  flowMode = computed<FlowMode>(() => (this.tokenStore.hasPaidPlan() ? 'repeat' : 'first'));

  canManageSubscription = this.authStore.canManageSubscription;
  isFinance = this.authStore.isFinance;

  // Set by the payment-handler after a successful subscription checkout, read once
  // here when the dialog reopens on /payment so we can confirm the purchase. The stored
  // value is the plan type, empty when only add-ons were bought.
  private readonly purchasedPlanType = this.readAndClearPurchaseSuccess();
  purchaseSuccess = signal<boolean>(this.purchasedPlanType !== null);
  isAddonOnlyPurchase = this.purchasedPlanType === '';

  private readAndClearPurchaseSuccess(): string | null {
    const planType = sessionStorage.getItem(SUBSCRIPTION_PURCHASE_SUCCESS_KEY);
    if (planType !== null) {
      sessionStorage.removeItem(SUBSCRIPTION_PURCHASE_SUCCESS_KEY);
    }
    return planType;
  }

  logout(): void {
    this.authStore.logout();
  }

  createCheckout({ plan, addons }: { plan: Product | null; addons: Product[] }): void {
    // PaymentScope has no value for buying an add-on against a plan you already hold
    // (PLAN, TOP_UP, REPORT_UPSELL). PLAN is sent for now; the scope for an add-on-only
    // purchase is an open question with the backend.
    // With no plan, metadata is {}: the success URL then carries ?scope=PLAN with no
    // planType, payment-handler.component.ts still takes the PaymentScope.PLAN branch, and
    // the missing planType is what switches both success screens (payment-completed-overlay
    // and the finance success state above) to their add-on-only copy.
    const metadata = plan
      ? {
          [PaymentMetadata.PLAN_TYPE]: plan.name?.toLocaleLowerCase(),
          [PaymentMetadata.IMAGE]: plan.images?.[0],
        }
      : {};

    const checkoutItems = [...(plan ? [plan] : []), ...addons].map((product) => ({
      priceId: product.defaultPrice.id,
      quantity: 1,
    }));

    this.tokenStore.createCheckoutSession({
      scope: PaymentScope.PLAN,
      checkoutItems,
      metadata,
    });
  }
}
